import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [ "form", "modal", "modalTitle", "methodField", "deleteButton" ]
  static values = {
    createUrl: String,
    canManage: { type: Boolean, default: false }
  }

  connect() {
    if (!this.hasModalTarget) return

    this.modalInstance = Modal.getOrCreateInstance(this.modalTarget)
    if (this.hasFormTarget && this.formTarget.querySelector(".alert-danger")) {
      this.modalInstance.show()
    }
  }

  disconnect() {
    this.modalInstance?.dispose()
  }

  openNew(event) {
    this.resetToCreate()
    const status = event.params.status
    if (status) this.field("status").value = status
    this.modalTitleTarget.textContent = "Add Task"
    this.hideDelete()
    this.modalInstance.show()
  }

  openEdit(event) {
    const { url, title, status, assignedToId, contactId, dueAt, canDestroy } = event.params

    this.formTarget.action = url
    this.formTarget.dataset.taskUrl = url
    this.methodFieldTarget.disabled = false
    this.field("title").value = title || ""
    this.field("status").value = status || "created"
    this.field("assigned_to_id").value = assignedToId || ""
    this.field("contact_id").value = contactId || ""
    this.field("due_at").value = dueAt || ""
    this.modalTitleTarget.textContent = "Edit Task"
    this.toggleDelete(canDestroy === true || canDestroy === "true")
    this.modalInstance.show()
  }

  submitted(event) {
    if (!event.detail.success) return

    this.modalInstance.hide()
    this.resetToCreate()
  }

  async toggleComplete(event) {
    event.stopPropagation()
    if (!this.canManageValue) return

    await this.patch(event.params.url, { status: event.currentTarget.checked ? "completed" : "created" })
  }

  dragstart(event) {
    if (!this.canManageValue) {
      event.preventDefault()
      return
    }

    const card = event.currentTarget
    event.dataTransfer.setData("text/plain", card.dataset.taskUrl)
    card.classList.add("nw-kanban__card--dragging")
  }

  dragend(event) {
    event.currentTarget.classList.remove("nw-kanban__card--dragging")
  }

  dragover(event) {
    if (!this.canManageValue) return

    event.preventDefault()
    event.currentTarget.classList.add("nw-kanban__cards--drop-target")
  }

  dragleave(event) {
    event.currentTarget.classList.remove("nw-kanban__cards--drop-target")
  }

  async drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("nw-kanban__cards--drop-target")
    if (!this.canManageValue) return

    const url = event.dataTransfer.getData("text/plain")
    const status = event.params.status
    if (url && status) await this.patch(url, { status })
  }

  async deleteTask() {
    const url = this.formTarget.dataset.taskUrl || this.formTarget.action
    if (!url || url === this.createUrlValue) return
    if (!window.confirm("Delete this task?")) return

    const response = await this.request(url, { method: "DELETE" })
    if (!response.ok) return

    await this.renderStream(response)
    this.modalInstance.hide()
    this.resetToCreate()
  }

  field(name) {
    return this.formTarget.elements[`task[${name}]`]
  }

  resetToCreate() {
    this.formTarget.reset()
    this.formTarget.action = this.createUrlValue
    delete this.formTarget.dataset.taskUrl
    this.methodFieldTarget.disabled = true
    this.modalTitleTarget.textContent = "Add Task"
    this.hideDelete()
    this.formTarget.querySelectorAll(".alert-danger").forEach((alert) => alert.remove())
  }

  hideDelete() {
    this.toggleDelete(false)
  }

  toggleDelete(show) {
    if (!this.hasDeleteButtonTarget) return

    this.deleteButtonTarget.hidden = !show
  }

  async patch(url, attributes) {
    const body = new URLSearchParams()
    Object.entries(attributes).forEach(([ key, value ]) => {
      body.append(`task[${key}]`, value)
    })

    const response = await this.request(url, { method: "PATCH", body })
    if (response.ok) await this.renderStream(response)
  }

  async request(url, { method, body }) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = {
      Accept: "text/vnd.turbo-stream.html",
      "X-CSRF-Token": token
    }

    if (body) headers["Content-Type"] = "application/x-www-form-urlencoded"

    return fetch(url, { method, headers, body, credentials: "same-origin" })
  }

  async renderStream(response) {
    const html = await response.text()
    if (html) window.Turbo.renderStreamMessage(html)
  }
}
